import { Stack } from '@mui/material';
import { useEffect, useRef } from 'react';

import CalendarField from '../../components/CalendarField/CalendarField';
import EmploymentSubtitle from '../../components/EmploymentSubtitle/EmploymentSubtitle';
import OccupationField from '../../components/OccupationField/OccupationField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const EMPLOYMENT_TYPE = 'coapplicantEmploymentType';
const EMPLOYED_SINCE = 'coapplicantEmployedSince';

/**
 * DK asks the co-applicant for an occupation and the date it started.
 *
 * It deliberately does **not** ask for a sector -- neither does the DK main-applicant page since PR #478
 * (LP-2850): `coapplicantEmploymentTypeSector`/`applicantEmploymentTypeSector` are not declared for DK in
 * cl-common's `credit-attributes.yaml`, because that attribute is Finland-only and held back until FI is
 * implemented (see that file's header comment). Sending it would be collecting an answer the backend cannot
 * compose. No `ShowDetailsModal` either -- the shared `dialog.employment.content` copy instructs the reader to
 * state their sector, which would contradict the form. The NO co-applicant page likewise renders no modal.
 *
 * The date field is `coapplicantEmployedSince`, the name DK's contract declares.
 */
const CoApplicantEmploymentPageDK = () => {
    useComponentTracker('CoApplicantEmploymentPageDK');

    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const { coApplicantTotalSections, coApplicantTotalAboutYouPages } = usePagesConfig();
    const { currentPage } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();

    const employmentType = useAppSelector(selectFieldValue)(EMPLOYMENT_TYPE);

    const requiredFields = [EMPLOYMENT_TYPE];
    if (employmentType) {
        requiredFields.push(EMPLOYED_SINCE);
    }
    const validationErrors = useValidation(requiredFields);

    // Clear the date whenever the occupation actually changes, so a date entered for a previous occupation
    // cannot be submitted against the new one -- "employed since" means nothing once the answer moves from
    // e.g. EMPLOYEE to PENSIONER, and `EmploymentSubtitle` relabels the field underneath it.
    //
    // `useFieldToggle` is deliberately not used here: its effect depends on a boolean, so it fires once when
    // an occupation is first chosen and never again on subsequent changes. Seeding the ref with the current
    // value also means a rehydrated answer survives back-navigation onto this page instead of being wiped.
    const previousEmploymentType = useRef(employmentType);
    useEffect(() => {
        if (previousEmploymentType.current !== employmentType) {
            previousEmploymentType.current = employmentType;
            dispatch(
                addOrUpdateFormField({ fieldName: EMPLOYED_SINCE, value: undefined, hasError: false, errorMessage: undefined }),
            );
        }
    }, [dispatch, employmentType]);

    useSetBannerType('bannerOne');

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next' && validationErrors.length) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });
            return false;
        }

        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.employment.title')} />
            <OccupationField label={translate('field.applicant-employment-type.label')} name={EMPLOYMENT_TYPE} />
            {employmentType && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <EmploymentSubtitle employmentType={employmentType as string} />
                    <CalendarField
                        name={EMPLOYED_SINCE}
                        label={translate('field.applicant-employment-date.label')}
                    />
                </Stack>
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default CoApplicantEmploymentPageDK;
